import {
  Component,
  OnDestroy,
  ViewChild,
  ElementRef,
  OnInit,
  HostListener,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { RvshareAppDetectionService } from '../../services/rvshare-app-detection.service';
import { ScreenSharingService } from '../../services/screen-sharing.service';
import { CodeInputComponent } from 'angular-code-input';
import { Subscription } from 'rxjs';
import { ModalService } from 'src/app/components/modals/modal.service';
import { ModalStackService } from 'src/app/components/modals/modal-stack.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { MessageType } from '../../types/message-type';
import { UIRouterGlobals } from '@uirouter/core';
import { StreamerWebsocketParamsService } from '../../types/streamer-params';

@Component({
  selector: 'screen-sharing',
  templateUrl: './screen-sharing.component.html',
  styleUrls: ['./screen-sharing.component.scss'],
  standalone: false,
})
export class ScreenSharingComponent implements OnDestroy, OnInit {
  @ViewChild('codeInput') codeInput: CodeInputComponent;
  @ViewChild('pinInput') pinInput: CodeInputComponent;
  @ViewChild('nameInput') nameInput: ElementRef;

  CODE_LENGTH = 6 as const;
  PING_LENGTH = 4 as const;

  joinCode = '';
  memberDisplayName = '';

  joined: boolean;
  paused: boolean;
  stopped: boolean;

  requireSecurityPin: boolean = false;
  securityPin: string = '';

  requireAuthentication: boolean = false;

  joinedSubscription: Subscription;
  invitedSubscription: Subscription;
  joinAttempted = false;
  private transitionSubscription: Function;

  private streamerWebsocketParamsService = inject(
    StreamerWebsocketParamsService,
  );

  @HostListener('window:hashchange') onHashChange() {
    // The page that was already open is in a session, so leave it (without sending the app deep links)
    if (this.showAppConnectionError()) {
      this.screenSharingService.resetAfterAppError();
    }
  }

  // The RV Share app reports a connection error in the URL hash, either on the page already open (hashchange) or on a
  // fresh page it opens after clearing its task (ngOnInit). Returns whether there was one.
  private showAppConnectionError(): boolean {
    if (!this.rvshareAppDetectionService.isScreenShareApp()) {
      return false;
    }

    const error = this.rvshareAppDetectionService.getConnectionError();
    if (error) {
      this.screenSharingService.channelError = error;
    }
    return !!error;
  }

  get moderatorName(): string {
    if (!this.screenSharingService.isModerated) {
      return '';
    }
    const mods = this.screenSharingService.participants.filter(
      (item) => item.memberType === 'moderator',
    );
    let label = 'your moderator';
    if (mods.length === 1) {
      return mods[0].memberDisplayName
        ? `${label}: <strong>${mods[0].memberDisplayName}</strong>`
        : label;
    } else if (mods.length > 1) {
      label = `one of ${label}s`;
      const names = mods
        .map((item) => item.memberDisplayName)
        .filter((item) => !!item)
        .map((name) => `<strong>${name}</strong>`);
      return names.length > 0 ? `${label}: ${names.join(', ')}` : label;
    }
    return label;
  }

  constructor(
    protected screenSharingService: ScreenSharingService,
    protected rvshareAppDetectionService: RvshareAppDetectionService,
    private modalService: ModalService,
    protected userStateService: UserStateService,
    private changeDetectorRef: ChangeDetectorRef,
    private uiRouterGlobals: UIRouterGlobals,
  ) {}

  ngOnInit(): void {
    // Only show it: a fresh page has no session to reset. Before _reset(), which removes the error from the URL.
    this.showAppConnectionError();
    this._reset();
    if (!this.rvshareAppDetectionService.isScreenShareApp()) {
      try {
        this.rvshareAppDetectionService.resetScreenShareApp();
      } catch (error) {
        console.error('Failed to reset screen share app state:', error);
      }
    }

    this.joinedSubscription = this.screenSharingService.joinedSubject.subscribe(
      (value) => {
        if (value === MessageType.JOINED_CHANNEL) {
          // In the RV Share app the stream lives in the app, which keeps sharing while this page's socket rejoins
          this.stopped = this.rvshareAppDetectionService.isScreenShareApp()
            ? !this.screenSharingService.memberIsSharing
            : !this.screenSharingService.mediaStream;
          this.joined = true;
          this.requireSecurityPin = false;
          this.requireAuthentication = false;
        } else if (value === MessageType.ANSWER) {
          this.stopped = false;
          this.paused = false;
        } else if (value === MessageType.CLIENT_STREAM_CLOSED) {
          this.stopped = true;
          this.paused = false;
        } else if (
          value === MessageType.CHANNEL_CLOSED ||
          value === MessageType.ERROR
        ) {
          this.requireAuthentication = false;
          this.joined = false;

          ModalStackService.closeAll();

          setTimeout(() => {
            this.changeDetectorRef.detectChanges();
            this.codeInput?.focusOnField(0);
          });
        } else if (value === MessageType.REQUEST_PIN) {
          this.joinAttempted = false;
          this.requireSecurityPin = true;
        } else if (value === MessageType.REQUEST_AUTHENTICATION) {
          this.joinAttempted = false;
          this.requireAuthentication = true;
        }

        this.changeDetectorRef.detectChanges();
      },
    );

    this.invitedSubscription =
      this.screenSharingService.invitedSubject.subscribe((value) => {
        if (!this.joined) {
          return;
        }

        if (value) {
          this.share();
        } else {
          this.stop();
        }
      });

    if (
      this.uiRouterGlobals.params.joinCode &&
      this.uiRouterGlobals.params.memberDisplayName
    ) {
      this.joinCode = this.uiRouterGlobals.params.joinCode;
      this.memberDisplayName = this.uiRouterGlobals.params.memberDisplayName;
      this.join();
    }
  }

  _reset() {
    this.stopped = false;
    this.paused = false;
    this.rvshareAppDetectionService.resetConnectionError();
  }

  ngOnDestroy(): void {
    this.screenSharingService.reset();
    this.joinedSubscription.unsubscribe();
    this.invitedSubscription.unsubscribe();
    if (this.transitionSubscription) {
      this.transitionSubscription();
    }
  }

  isDisplayMediaAvailable(): boolean {
    return (
      'navigator' in window &&
      'mediaDevices' in navigator &&
      'getDisplayMedia' in navigator.mediaDevices
    );
  }

  isScreenSharingSupported(): boolean {
    return (
      this.isDisplayMediaAvailable() ||
      this.rvshareAppDetectionService.isScreenShareApp()
    );
  }

  join() {
    this.joinAttempted = true;
    const token =
      this.userStateService.isLoggedIn() &&
      this.userStateService.getAccessToken()?.access_token;
    const websocketParams = this.streamerWebsocketParamsService.create(
      this.memberDisplayName,
      this.joinCode,
      this.securityPin,
      token,
    );
    this._reset();

    if (this.requireSecurityPin && this.securityPin.length < this.PING_LENGTH) {
      this.pinInput.focusOnField(
        this.securityPin ? this.securityPin.length : 0,
      );
    } else if (this.joinCode.length < this.CODE_LENGTH) {
      this.codeInput.focusOnField(this.joinCode.length);
    } else if (!this.memberDisplayName) {
      this.nameInput.nativeElement.focus();
    } else {
      this.screenSharingService.join(websocketParams);
    }
  }

  joinCodeChanged(code: string) {
    this.joinCode = code.toUpperCase();
  }

  joinCodeCompleted() {
    if (!this.screenSharingService.joinCodeError) {
      this.nameInput.nativeElement.focus();
    }
  }

  securityPinChanged(code: string) {
    this.securityPin = code.toUpperCase();
  }

  start() {
    this.paused = false;
    this.stopped = true;

    this.screenSharingService.start();
  }

  pause() {
    this.paused = true;
    this.stopped = false;

    this.screenSharingService.pause();
  }

  resume() {
    this.paused = false;
    this.stopped = false;

    this.screenSharingService.resume();
  }

  stop() {
    if (!this.stopped) {
      this.screenSharingService.stop();
    }

    this.stopped = true;
    this.paused = false;
  }

  end() {
    this.modalService
      .confirmDanger(
        'Exit Session?',
        'You will need to enter the session code again to re-enter this room.',
        'Exit Room',
        'Cancel',
      )
      .then(() => {
        if (this.screenSharingService.isModerated) {
          this.screenSharingService.leave();
        } else {
          this.screenSharingService.reset();
        }
      });
  }

  share() {
    this.modalService
      .confirm(
        'Share your Screen?',
        'The moderator has invited you to share your screen. Please click "Share Now" to start sharing.',
        'Share Now',
        'Cancel',
      )
      .then(() => {
        this.screenSharingService.start();
      })
      .catch(() => {
        this.screenSharingService.stop();
      });
  }

  signedOut(successfull: boolean) {
    if (successfull) {
      this.screenSharingService.channelError = '';
      this.modalService.showMessage(
        'Signed out',
        'You have successfully signed out.',
      );
    } else {
      this.modalService.showMessage(
        'Error',
        'There was an error trying to sign you out. Please, try again.',
      );
    }
  }
}
